/**
 * Controller context helpers (owner: movement team): the per-tick ROLE of a player (ARCHITECTURE §5 Controls
 * "Modes"), speeds with the stat multipliers, input readers built on `player/input.ts`, and small movement
 * primitives. Pure and allocation-free.
 */
import { MOVE, MOTION, DT, SPECIALS_TUNING, approach, horizontal } from '@hf/shared';
import { BallState, BtnIndex, Phase, PlayerState, isKickCall } from '../enums';
import { hasBufferedPress, isHeld } from './input';
import { isQbMode } from '../selectors';
import { bigHeadActive, rocketBootsActive } from '../specials/query';
import type { InputHistory, PlayerSim, SimState } from '../state';

/** Exactly one applies to each player each tick. */
export const Role = {
  /** DEAD / CONVERSION / GAME_OVER: no new actions, locks run out, bodies settle. */
  INACTIVE: 0,
  /** PRE_SNAP, offense (shuffle behind the LOS; Action/▲ belong to the rules: snap / hard count). */
  PRE_OFFENSE: 1,
  /** PRE_SNAP, defense (shuffle; crossing the LOS is OFFSIDE). */
  PRE_DEFENSE: 2,
  /** Offense ball holder behind the LOS, no pass thrown yet (isQbMode). */
  QB: 3,
  /** Ball holder otherwise (past the LOS, after a catch, an interception returner). */
  CARRIER: 4,
  /** Everyone else during a live play (incl. the ball-less offense player while a pass is in the air). */
  DEFENDER: 5,
} as const;
export type Role = (typeof Role)[keyof typeof Role];

export const roleOf = (s: SimState, p: PlayerSim): Role => {
  const ph = s.rules.phase;
  if (ph === Phase.PRE_SNAP) return p.index === s.rules.offense ? Role.PRE_OFFENSE : Role.PRE_DEFENSE;
  if (ph !== Phase.RUSH_COUNT && ph !== Phase.LIVE) return Role.INACTIVE;
  const b = s.ball;
  if (b.state === BallState.HELD && b.holder === p.index)
    return isQbMode(s, p.index) ? Role.QB : Role.CARRIER;
  return Role.DEFENDER;
};

/** The ball is in this player's glove (held, or dead in his glove after the whistle). */
export const holdsBall = (s: SimState, p: PlayerSim): boolean =>
  s.ball.holder === p.index && (s.ball.state === BallState.HELD || s.ball.state === BallState.DEAD);

/** The other player. */
export const opponentOf = (s: SimState, p: PlayerSim): PlayerSim => s.players[p.index === 0 ? 1 : 0];

/**
 * Per-player speeds for the current controller pass (written by `loadSpeeds`; doubles travel through this
 * scratch, never through call returns, so nothing is boxed whatever the optimizer inlines).
 *   run   top ground speed: run speed × SPD × (carrier penalty) × (hesitation burst)
 *   dash  dash speed with the same multipliers (no burst)
 *   air   airborne drift cap: airMaxSpeed × SPD (× a speed bonus: throw & chase, kick coverage, pursuit)
 *   mul   the speed multiplier itself (`loadSpeedMul`)
 *   pursuit  whether `mul` is the pursuit bonus (it does not apply to dashes)
 */
export const spd = { run: 0.5, dash: 0.5, air: 0.5, mul: 0.5, pursuit: false };

export const loadSpeeds = (s: SimState, p: PlayerSim): void => {
  loadSpeedMul(s, p);
  // Rocket Boots: a flat burst while it runs. Big Head: a flat penalty (the "slower — run past" counter).
  const specialMul =
    (rocketBootsActive(p) ? SPECIALS_TUNING.rocketBoots.speedMul : 1) *
    (bigHeadActive(p) ? SPECIALS_TUNING.bigHead.speedMul : 1);
  const carry = spd.mul * specialMul;
  spd.run = MOVE.runSpeed * p.stats.speedMul * carry * (p.burstFrames > 0 ? MOVE.hesitationBurstMul : 1);
  // a pursuer's dash is a plain dash (the bonus is on his run): chained chase-down dashes were 17+ yd/s
  spd.dash = MOVE.dashSpeed * p.stats.speedMul * (spd.pursuit ? 1 : carry);
  spd.air = MOVE.airMaxSpeed * p.stats.speedMul * (carry > 1 ? carry : 1);
};

/**
 * `spd.mul` = the player's speed multiplier (1 = none; written to the scratch, never returned, so nothing is boxed):
 *  - the ball carrier: MOVE.carrierSpeedMul;
 *  - throw & chase (MOTION.chaseSpeedMul): the thrower of a live FORWARD pass still in the air runs his route
 *    faster (Hail Mary: chaseSpeedMulHailMary);
 *  - kick coverage (MOTION.coverageSpeedMul): the kicker (Classic kickoff / punt / onside) pursuing the returner
 *    once the receiver holds the kick, so one missed tackle is not a free return touchdown;
 *  - pursuit (MOTION.pursuit, see `pursuing`): chasing the carrier from behind in a live scrimmage play — the
 *    CARRIER's top speed × (1 + a flat bonus + a ramp with the gap), never slower than my own: a carrier who wins one
 *    exchange gains real yards but is run down from ≈ 8 yd within ≈ 20 yd, whoever plays whom, and has to win
 *    another exchange (or reach the end zone first).
 */
const loadSpeedMul = (s: SimState, p: PlayerSim): void => {
  spd.pursuit = false;
  if (holdsBall(s, p)) {
    spd.mul = MOVE.carrierSpeedMul;
    return;
  }
  const b = s.ball;
  if (b.state === BallState.AIR) {
    if (!b.forwardPass || b.thrownBy !== p.index || b.kick) spd.mul = 1;
    else spd.mul = b.hailMary ? MOTION.chaseSpeedMulHailMary : MOTION.chaseSpeedMul;
    return;
  }
  if (kickCoverage(s, p)) {
    spd.mul = MOTION.coverageSpeedMul;
    return;
  }
  if (!pursuing(s, p)) {
    spd.mul = 1;
    return;
  }
  const pu = MOTION.pursuit;
  const r = s.rules;
  const c = s.players[b.holder === 0 ? 0 : 1];
  const gd = b.holder === r.offense ? r.attackDir : -r.attackDir;
  const k = (gd * (c.x - p.x) - pu.minGap) / (pu.fullGap - pu.minGap);
  // relative to the CARRIER's top speed (never below my own): every pairing closes at the same rate, so a slow
  // head is neither a worse pursuer nor easier prey than a fast one (fair by construction)
  const rel = (c.stats.speedMul * MOVE.carrierSpeedMul) / p.stats.speedMul;
  spd.mul = Math.max(1, rel * (1 + pu.bonus + pu.rampBonus * Math.min(k, 1)));
  spd.pursuit = true;
};

/** The kicker covering a Classic kick the receiver now holds (kickoff / punt / onside). */
const kickCoverage = (s: SimState, p: PlayerSim): boolean => {
  const b = s.ball;
  const r = s.rules;
  return (
    b.state === BallState.HELD &&
    b.holder >= 0 &&
    b.holder !== p.index &&
    p.index === r.offense &&
    isKickCall(r.playCall) &&
    (r.phase === Phase.LIVE || r.phase === Phase.RUSH_COUNT)
  );
};

/**
 * Pursuit (MOTION.pursuit): in a LIVE scrimmage play (not a kick), `p` chases the ball carrier FROM BEHIND — the
 * carrier is more than `minGap` yd past him toward the carrier's goal (an interception returner's goal too) and
 * `p` faces that way, and he has not just lost an exchange (`beatenTick` within `graceFrames`). He runs, dashes and
 * drifts faster (`spd.mul`), and a running dive keeps its momentum through the wind-up. Also the render / audio
 * effort cue.
 */
export const pursuing = (s: SimState, p: PlayerSim): boolean => {
  const b = s.ball;
  const r = s.rules;
  if (b.state !== BallState.HELD || b.holder < 0 || b.holder === p.index) return false;
  if (r.phase !== Phase.LIVE || isKickCall(r.playCall)) return false;
  // grace: just beaten by a counter-square exchange — the won read is worth a real head start
  if (p.beatenTick >= 0 && s.tick - p.beatenTick < MOTION.pursuit.graceFrames) return false;
  const gd = b.holder === r.offense ? r.attackDir : -r.attackDir;
  return p.facing === gd && gd * (s.players[b.holder === 0 ? 0 : 1].x - p.x) > MOTION.pursuit.minGap;
};

/** Speed multiplier of `p` this tick (see `loadSpeedMul`; 1 = none). Read-only helper for the CPU / VFX. */
export const chaseMul = (s: SimState, p: PlayerSim): number => {
  loadSpeedMul(s, p);
  return spd.mul;
};

/** Horizontal input (-1, 0, 1; L+R = neutral). */
export const dirOf = (h: InputHistory): number => horizontal(h.cur);

/**
 * Grounded horizontal control toward `dir × spd.run × mul` (accelerate with runAccel, brake/turn with runDecel).
 * A speed above the target (after a dash or burst) bleeds off with runDecel. `dir` 0 = brake to a stop.
 */
export const groundControl = (p: PlayerSim, dir: number, mul: number): void => {
  const target = dir * spd.run * mul;
  const v = p.vx;
  const turning = dir !== 0 && v !== 0 && (v > 0 ? dir < 0 : dir > 0);
  const over = dir !== 0 && (v > 0 ? v > target && dir > 0 : v < target && dir < 0);
  const rate = dir === 0 || turning || over ? MOVE.runDecel : MOVE.runAccel;
  p.vx = approach(v, target, rate * DT);
};

/** Pre-snap stance shuffle toward `dir × shuffleSpeed × SPD`. */
export const shuffleControl = (p: PlayerSim, dir: number): void => {
  const target = dir * MOVE.shuffleSpeed * p.stats.speedMul;
  p.vx = approach(p.vx, target, (dir === 0 ? MOVE.runDecel : MOVE.runAccel) * DT);
};

/** Airborne drift: steer toward airMaxSpeed × SPD; never brakes momentum the player is pushing along. */
export const airControl = (p: PlayerSim, dir: number): void => {
  if (dir === 0) return;
  const max = spd.air;
  if (dir > 0 ? p.vx >= max : p.vx <= -max) return;
  p.vx = approach(p.vx, dir * max, MOVE.airAccel * DT);
};

/** Brake horizontally (grounded: `decel`; airborne: momentum is kept, gravity does the rest). */
export const brake = (p: PlayerSim, decel: number): void => {
  if (p.grounded) p.vx = approach(p.vx, 0, decel * DT);
};

/** Stun/stumble/tackled/pose deceleration. */
export const settle = (p: PlayerSim): void => brake(p, MOTION.stunDecel);

/**
 * Chord SPIN: ▼ and Action both pressed (still buffered, i.e. not consumed by another move) within
 * MOVE.chordWindowFrames of each other, and both held.
 */
export const chordReady = (h: InputHistory): boolean => {
  if (!hasBufferedPress(h, BtnIndex.D) || !hasBufferedPress(h, BtnIndex.A)) return false;
  if (!isHeld(h, BtnIndex.D) || !isHeld(h, BtnIndex.A)) return false;
  const gap = h.lastPress[BtnIndex.D]! - h.lastPress[BtnIndex.A]!;
  return (gap < 0 ? -gap : gap) <= MOVE.chordWindowFrames;
};

/**
 * Chord cancel from a state the FIRST button already started (TRUCK_CHARGE by ▼, STIFF_ARM / CHARGING_THROW by
 * Action): the `other` button is newly buffered, pressed within the window of `first`'s press, and both held.
 */
export const chordCancelReady = (h: InputHistory, first: BtnIndex, other: BtnIndex): boolean => {
  if (!hasBufferedPress(h, other) || !isHeld(h, first) || !isHeld(h, other)) return false;
  const lf = h.lastPress[first]!;
  if (lf < 0) return false;
  const gap = h.lastPress[other]! - lf;
  return gap >= 0 && gap <= MOVE.chordWindowFrames;
};

/** Double-tap of direction button `b`: its latest press is buffered and came ≤ doubleTapWindow after the previous. */
export const doubleTapReady = (h: InputHistory, b: BtnIndex): boolean =>
  hasBufferedPress(h, b) &&
  isHeld(h, b) &&
  h.prevPress[b]! >= 0 &&
  h.lastPress[b]! - h.prevPress[b]! <= MOVE.doubleTapWindow;

/** States in which the player stands/moves freely on the ground. */
export const isGroundNeutral = (st: PlayerState): boolean =>
  st === PlayerState.IDLE || st === PlayerState.RUN;
